<template>
  <Sidebar
    class="border-0 bg-muted h-screen"
    collapsible="none"
  >
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem class="pt-4">
          <RouterLink
            v-if="generalSettings?.[0]?.logo_url"
            to="/"
            class="block rounded-md px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <img
              :src="generalSettings[0].logo_url"
              alt="Home"
            >
          </RouterLink>
          <SidebarMenuButton v-else>
            <LayoutGrid />
            <h6 class="font-semibold">
              ComplyOne Inc.
            </h6>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>

    <div
      v-if="totalNavItems > 8"
      class="px-4 pt-3"
    >
      <div class="relative">
        <Search class="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          v-model="navQuery"
          type="search"
          placeholder="Search menu..."
          class="h-8 bg-background pl-8 text-sm"
        />
      </div>
    </div>

    <SidebarContent class="pt-3 gap-0 [scrollbar-width:thin]">
      <SidebarMenu>
        <SidebarGroup
          v-for="group in generalNav"
          :key="group.label"
          class="space-y-1"
        >
          <SidebarGroupLabel v-if="group.label !== 'MAIN'">
            {{ group.label }}
          </SidebarGroupLabel>

          <SidebarMenuItem
            v-for="item in group.items"
            :key="item.title"
          >
            <SidebarMenuButton
              :disabled="item.disabled"
              :is-active="item.active"
              @click="router.push(item.url)"
            >
              <component
                :is="item.icon"
                size="18"
              />
              <span>{{ item.title }}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarGroup>
      </SidebarMenu>


      <!-- Plain expand/collapse — avoid reka-ui Collapsible Presence + ResizeObserver
           crashing when groups mount closed (open=false → non-Element observe). -->
      <template v-if="authStore.isSuperAdmin ? insideWorkspace() : true">
        <SidebarGroup
          v-for="(group, index) in visibleWorkspaceNav"
          :key="group.label"
          class="py-1"
        >
          <SidebarGroupLabel
            as="button"
            type="button"
            class="w-full cursor-pointer justify-between text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
            :aria-expanded="isGroupOpen(group)"
            @click="setGroupOpen(group.label, !isGroupOpen(group))"
          >
            <span>{{ group.label }}</span>
            <ChevronRight
              class="size-3.5 transition-transform duration-200"
              :class="{ 'rotate-90': isGroupOpen(group) }"
            />
          </SidebarGroupLabel>

          <SidebarMenu
            v-show="isGroupOpen(group)"
            class="gap-0.5"
          >
            <SidebarMenuItem
              v-for="item in group.items"
              :key="item.title"
            >
              <SidebarMenuButton
                :disabled="item.disabled"
                :is-active="item.active"
                class="data-[active=true]:font-medium data-[active=true]:shadow-sm"
                @click="router.push(item.url)"
              >
                <component
                  :is="item.icon"
                  size="18"
                />
                <span class="truncate">{{ item.title }}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <template v-if="index === 0 && !permittedModules.length && loadingModules">
              <Skeleton
                v-for="x in 6"
                :key="x"
                class="w-full h-8 dark:bg-sidebar-foreground/10"
              />
            </template>
            <template v-else-if="group.label === 'Forms' && !group.items.length && loadingFormTypes">
              <Skeleton
                v-for="x in 2"
                :key="x"
                class="w-full h-8 dark:bg-sidebar-foreground/10"
              />
            </template>
          </SidebarMenu>
        </SidebarGroup>
      </template>

      <SidebarGroup
        v-if="!insideWorkspace() && visibleFooterNav.length"
        class="py-1"
      >
        <SidebarGroupLabel
          as="button"
          type="button"
          class="w-full cursor-pointer justify-between text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
          :aria-expanded="isGroupOpen(adminGroup)"
          @click="setGroupOpen(adminGroup.label, !isGroupOpen(adminGroup))"
        >
          {{ adminGroup.label }}
          <ChevronRight
            class="size-3.5 transition-transform duration-200"
            :class="{ 'rotate-90': isGroupOpen(adminGroup) }"
          />
        </SidebarGroupLabel>

        <SidebarMenu
          v-show="isGroupOpen(adminGroup)"
          class="gap-0.5"
        >
          <SidebarMenuItem
            v-for="item in visibleFooterNav"
            :key="item.title"
          >
            <SidebarMenuButton
              :is-active="item.active"
              @click="navigateTo(item)"
            >
              <component
                :is="item.icon"
                size="18"
              />
              <span class="truncate">{{ item.title }}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>

      <SidebarGroup
        v-if="!insideWorkspace() && resolvedToolsNav.length"
        class="py-1"
      >
        <SidebarGroupLabel
          as="button"
          type="button"
          class="w-full cursor-pointer justify-between uppercase tracking-wide hover:text-sidebar-foreground"
          :aria-expanded="isGroupOpen(toolsGroup)"
          @click="setGroupOpen(toolsGroup.label, !isGroupOpen(toolsGroup))"
        >
          {{ toolsGroup.label }}
          <ChevronRight
            class="size-3.5 transition-transform duration-200"
            :class="{ 'rotate-90': isGroupOpen(toolsGroup) }"
          />
        </SidebarGroupLabel>

        <SidebarMenu
          v-show="isGroupOpen(toolsGroup)"
          class="gap-0.5"
        >
          <SidebarMenuItem
            v-for="item in resolvedToolsNav"
            :key="item.title"
          >
            <SidebarMenuButton
              :is-active="item.active"
              @click="navigateTo(item)"
            >
              <component
                :is="item.icon"
                size="18"
              />
              <span class="truncate">{{ item.title }}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
    </SidebarContent>

    <SidebarFooter class="pb-4 border-t">
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <SidebarMenuButton size="lg">
                <CAvatar :user="user" />
                <div class="grid flex-1 text-left text-sm">
                  <span class="truncate font-medium">{{ user.full_name }}</span>
                  <span class="truncate text-xs">{{ user.email }}</span>
                </div>
                <ChevronsUpDown class="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent class="w-56">
              <DropdownMenuItem>
                <CAvatar :user="user" />
                <div class="grid flex-1 text-left text-sm">
                  <span class="truncate font-medium">{{ user.full_name }}</span>
                  <span class="truncate text-xs">{{ user.email }}</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                class="mt-1"
                @click="$router.push(`/profile`)"
              >
                Edit Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                class="hover:!bg-destructive/5 hover:!text-destructive"
                @click="logout"
              >
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  </Sidebar>
</template>
<script setup lang="ts">
import { computed, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import { storeToRefs } from "pinia"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SquareTerminal,
  Layers,
  ArrowLeft,
  ScrollText,
  UsersRound,
  Settings,
  Lock,
  ClipboardList,
  LayoutGrid,
  ChevronsUpDown,
  ChevronRight,
  Search,
  Share2,
  Blocks,
  ClipboardCheck,
  Mail,
  MessageSquareText,
  FileCheck2,
  FileSignature,
  FileStack,
  GraduationCap,
  FileText,
  Bot,
  ScanSearch,
} from "lucide-vue-next"
import { Input } from "@/components/ui/input"
import CAvatar from "@/components/common/CAvatar.vue"
import { useAuthStore, useSettingsStore, useWorkspaceStore } from "@/stores"
import { useWorkspaceFormTypes } from "@/queries/questionnaires"
import { hasAnyPermission, insideWorkspace } from "@/lib/filters"
import { isOnboardingPartyType } from "@/lib/onboardingParties.js"
import Skeleton from "./ui/skeleton/Skeleton.vue"
import { useDashboardModules } from '@/queries/dashboard'

/* -------------------------------------------------------
 * Setup
 * ----------------------------------------------------- */

const route = useRoute()
const router = useRouter()

const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const settingsStore = useSettingsStore()

const { user } = storeToRefs(authStore) as any
const { generalSettings } = storeToRefs(settingsStore) as any
const { permittedModules, loadingModules } = useDashboardModules()
const { formTypes, loadingFormTypes } = useWorkspaceFormTypes({
  enabled: () => hasAnyPermission(["view_workspace_form_template"]),
})

/* -------------------------------------------------------
 * Nav item factory
 * ----------------------------------------------------- */

interface NavItemInput {
  title: string
  icon?: any
  path: string
  permissions?: any
  exact?: boolean
  workspace?: boolean
  disabled?: boolean
  /** Open in a new tab instead of vue-router (absolute http(s) URLs). */
  external?: boolean
}

interface NavItem extends NavItemInput {
  exact: boolean
  workspace: boolean
  disabled: boolean
  external: boolean
}

interface ResolvedNavItem extends NavItem {
  url: string
  active: boolean
}

interface NavGroup {
  label: string
  items: ResolvedNavItem[]
}

function navItem({
  title,
  icon,
  path,
  permissions,
  exact = false,
  workspace = false,
  disabled = false,
  external = false,
}: NavItemInput): NavItem {
  return {
    title,
    icon,
    path,
    permissions,
    exact,
    workspace,
    disabled,
    external,
  }
}

/** Policy AI SPA origin from VITE_POLICY_AI_API_URL (strip trailing /api/v1). */
function policyAiAppOrigin() {
  const api = String(import.meta.env.VITE_POLICY_AI_API_URL || "").replace(/\/$/, "")
  return api.replace(/\/api(?:\/v\d+)?$/i, "") || "https://policyai.complyone.laradev.cloud"
}

/* -------------------------------------------------------
 * Raw navigation definitions
 * ----------------------------------------------------- */

const mainNav = computed(() => [
  navItem({
    title: "Admin Dashboard",
    icon: SquareTerminal,
    path: "/admin-dashboard",
    exact: true,
    permissions: ["admin"],
  }),
  navItem({
    title: route.params.workspace_id ? "Back to Workspaces" : "Workspaces",
    icon: route.params.workspace_id ? ArrowLeft : Layers,
    path: "/workspaces",
    permissions: ["admin"],
  }),
])

const formsNav = [
  navItem({
    title: "Workspace Onboarding",
    icon: ClipboardList,
    path: "/questionnaires/workspace",
    permissions: ["admin"],
  }),
  // navItem({
  //   title: "Vendor Onboarding",
  //   icon: ClipboardList,
  //   path: "/onboarding/vendor",
  //   permissions: ["admin"],
  // }),
]

const moduleNav = computed(() => {
  const dashboardItem = navItem({
    title: "Dashboard",
    icon: SquareTerminal,
    path: "dashboard",
    workspace: true,
    permissions: ["allowAuthenticated"],
  })

  const modules = permittedModules.value
    .filter((module: any) => module.to)
    .map((module: any) => navItem({
      title: module.title,
      icon: module.icon,
      path: module.to,
      workspace: true,
      permissions: module.permissions ?? module.permission,
      disabled: !module.workspace_is_enabled
    }))

  // Assignment-scoped audits (Auditee / Analyst) — not the workspace Audits module.
  const myAudits = authStore.isAuditeeAssignee
    ? [navItem({
        title: "My Audits",
        icon: ClipboardCheck,
        path: "/auditee/audits",
        permissions: ["allowAuthenticated"],
      })]
    : []

  return [dashboardItem, ...myAudits, ...modules]
})

const workspaceFormNav = computed(() => {
  return formTypes.value
    .filter((t: any) => t?.type)
    .map((t: any) =>
      navItem({
        title: t.name || t.type,
        icon: ClipboardList,
        path: `questionnaires/${t.type}`,
        workspace: true,
        permissions: ["view_workspace_form_template"],
      }),
    )
})

/** Party management for dynamic form types (not vendors / employees / workspace). */
const onboardingPartyNav = computed(() => {
  return formTypes.value
    .filter((t: any) => isOnboardingPartyType(t?.type))
    .map((t: any) =>
      navItem({
        title: t.name || t.type,
        icon: ClipboardList,
        path: `onboarding-parties/${t.type}`,
        workspace: true,
        permissions: ["edit_workspace"],
      }),
    )
})

const teamAccessNav = [
  navItem({
    title: "Users",
    icon: UsersRound,
    path: "users",
    workspace: true,
    permissions: ["view_user"],
  }),
  navItem({
    title: "Sharing",
    icon: Share2,
    path: "sharing",
    workspace: true,
    permissions: ["share_data"],
  }),
  navItem({
    title: "Join Requests",
    icon: Mail,
    path: "join-requests",
    workspace: true,
    permissions: ["admin",""],
  }),
  navItem({
    title: "Approval Requests",
    icon: ClipboardCheck,
    path: "approval-requests",
    workspace: true,
    permissions: ["view_approval_request"],
  }),
]

const documentsNav = [
  navItem({
    title: "Document Templates",
    icon: ScrollText,
    path: "document-templates",
    workspace: true,
    permissions: ["assign_employee_documents"],
  }),
  navItem({
    title: "Signature Requests",
    icon: FileSignature,
    path: "document-templates/signature-requests",
    workspace: true,
    permissions: ["assign_employee_documents"],
  }),
  navItem({
    title: "Audit Templates",
    icon: FileCheck2,
    path: "audit-templates",
    workspace: true,
    permissions: ["view_audit", "view_audit_template"],
  }),
]

const workspaceConfigNav = [
  navItem({
    title: "Workspace Settings",
    icon: Settings,
    path: "workspace-settings",
    workspace: true,
    // permissions: ["view_training"], // TODO: remove this after testing
    permissions: ["view_workspace"],
  }),
]

const footerNav = [
  navItem({
    title: "Permissions",
    icon: Lock,
    path: "/settings/permissions",
    permissions: ["admin"],
  }),
  // navItem({
  //   title: "Policy Types",
  //   icon: ClipboardList,
  //   path: "/settings/policy-types",
  //   permissions: ["admin"],
  // }),
  navItem({
    title: "Inquiries",
    icon: ClipboardList,
    path: "/inquiries",
    permissions: ["admin"],
  }),
  navItem({
    title: "Feedbacks",
    icon: MessageSquareText,
    path: "/settings/feedbacks",
    permissions: ["admin"],
  }),
  navItem({
    title: "Modules",
    icon: Blocks,
    path: "/settings/modules",
    permissions: ["admin"],
  }),
  navItem({
    title: "Form Templates",
    icon: FileStack,
    path: "/settings/form-templates",
    permissions: ["admin"],
  }),
  navItem({
    title: "Training Templates",
    icon: GraduationCap,
    path: "/settings/training-templates",
    permissions: ["admin"],
  }),
  navItem({
    title: "All Settings",
    icon: Settings,
    path: "/settings/general",
    permissions: ["admin"],
  }),
]

const toolsNav = computed(() => {
  if (!authStore.isSuperAdmin) return []
  const origin = policyAiAppOrigin()
  https://policyai.complyone.laradev.cloud/intake
  return [
    navItem({
      title: "Policy Builder",
      icon: FileText,
      path: `${origin}/intake`,
      external: true,
    }),
    navItem({
      title: "Audit AI",  
      icon: Bot,
      path: `${origin}/audit`,
      external: true,
    }),
    navItem({
      title: "Screening",
      icon: ScanSearch,
      path: "/settings/aml-screening",
    }),
  ]
})

/* -------------------------------------------------------
 * Nav resolver (single source of truth)
 * ----------------------------------------------------- */

function resolveNavItem(item: NavItem | null | undefined): ResolvedNavItem | null {
  if (!item || item.path == null || item.path === "") return null
  if (item.permissions && !hasAnyPermission(item.permissions)) return null
  if (item.path == null || typeof item.path !== "string") return null

  if (item.external || /^https?:\/\//i.test(item.path)) {
    return {
      ...item,
      external: true,
      url: item.path,
      active: false,
    }
  }

  // Always scope workspace links to /workspaces/:id — short aliases were removed.
  const workspaceId =
    route.params.workspace_id || workspaceStore.getWorkspaceID
  const basePath =
    item.workspace && workspaceId ? `/workspaces/${workspaceId}` : ""

  const url = item.path.startsWith("/")
    ? item.path
    : `${basePath}/${item.path}`

  const active = item.exact
    ? route.path === url
    : route.path.startsWith(url)

  return {
    ...item,
    url,
    active,
  }
}

function buildGroup(label: string, items: NavItem[]): NavGroup | null {
  const resolved = items
    .map(resolveNavItem)
    .filter((item): item is ResolvedNavItem => Boolean(item))
  return resolved.length ? { label, items: resolved } : null
}

/* -------------------------------------------------------
 * Computed navigation groups
 * ----------------------------------------------------- */

const generalNav = computed(() =>
  [
    buildGroup("MAIN", mainNav.value),
    !route.params.workspace_id &&
    buildGroup("Forms", formsNav),
  ].filter((group): group is NavGroup => Boolean(group))
)

const workspaceNav = computed(() => {
  const formsGroup = buildGroup("Forms", workspaceFormNav.value)
  // Keep the Forms section visible while types load so the layout doesn't jump.
  const forms: NavGroup | null =
    formsGroup ??
    (loadingFormTypes.value && hasAnyPermission(["view_workspace_form_template"])
      ? { label: "Forms", items: [] }
      : null)

  const onboardingGroup = buildGroup("Onboarding", onboardingPartyNav.value)

  return [
    buildGroup("Modules", moduleNav.value),
    forms,
    onboardingGroup,
    buildGroup("Team & Access", teamAccessNav),
    buildGroup("Documents", documentsNav),
    buildGroup("Workspace", workspaceConfigNav),
  ].filter((group): group is NavGroup => Boolean(group))
})

const resolvedFooterNav = computed(() =>
  footerNav
    .map(resolveNavItem)
    .filter((item): item is ResolvedNavItem => Boolean(item))
)

/** Filters nav items by the sidebar search box; empty groups are dropped. */
const visibleWorkspaceNav = computed(() => {
  const q = navQuery.value.trim().toLowerCase()
  if (!q) return workspaceNav.value
  return workspaceNav.value
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.title.toLowerCase().includes(q)),
    }))
    .filter((group) => group.items.length)
})

const visibleFooterNav = computed(() => {
  const q = navQuery.value.trim().toLowerCase()
  return q
    ? resolvedFooterNav.value.filter((item) => item.title.toLowerCase().includes(q))
    : resolvedFooterNav.value
})

const totalNavItems = computed(
  () =>
    workspaceNav.value.reduce((sum, group) => sum + group.items.length, 0) +
    resolvedFooterNav.value.length,
)

const resolvedToolsNav = computed(() =>
  toolsNav.value
    .map(resolveNavItem)
    .filter((item): item is ResolvedNavItem => Boolean(item))
)

/* -------------------------------------------------------
 * Collapsible groups
 * ----------------------------------------------------- */

/** Bumped so secondary workspace groups reset to collapsed for existing sessions. */
const GROUP_STATE_KEY = "sidebar-groups-v3"
const adminGroup = { label: "Administration" }
const toolsGroup = { label: "Tools" }
/** Only Modules stays open by default in workspace view; the rest stay closed. */
const DEFAULT_OPEN_GROUPS = new Set(["Modules"])

function loadGroupState(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(GROUP_STATE_KEY) || "{}")
  } catch {
    return {}
  }
}

const navQuery = ref("")

const groupState = ref<Record<string, boolean>>(loadGroupState())

function isGroupOpen(group: { label: string; items?: ResolvedNavItem[] }): boolean {
  if (navQuery.value.trim()) return true
  const saved = groupState.value[group.label]
  if (typeof saved === "boolean") return saved

  const items =
    group.items ??
    (group === adminGroup
      ? resolvedFooterNav.value
      : group === toolsGroup
        ? resolvedToolsNav.value
        : [])

  // Auto-expand when the current page lives in this group.
  if (items.some((item) => item.active)) return true

  return DEFAULT_OPEN_GROUPS.has(group.label)
}

function setGroupOpen(label: string, value: boolean) {
  groupState.value = { ...groupState.value, [label]: value }
  try {
    localStorage.setItem(GROUP_STATE_KEY, JSON.stringify(groupState.value))
  } catch {
    // storage unavailable — state stays in memory only
  }
}

/* -------------------------------------------------------
 * Actions
 * ----------------------------------------------------- */

const navigateTo = (item: ResolvedNavItem) => {
  if (item.external) {
    window.open(item.url, "_blank", "noopener,noreferrer")
    return
  }
  router.push(item.url)
}

const logout = () => authStore.Logout()
</script>
